import ExcelJS from "exceljs";

import { get_currency } from "./currency";
import {
    COMPARISON_FILTER,
    DEFAULT_COMPARISON,
    GROUP_COUNTRIES,
    GROUP_EXTRAS,
    GROUP_OFFERS,
    GROUP_ROOM,
    GROUP_VOUCHERS,
} from "./filters";
import { HOTELS_INFO } from "../configuration";

export const fullExport = (config, data, hotelID, options = {}) => {
    const exportTitle = options.title ?? "Full data export";
    const fileName = options.fileName ?? "FullDataExport.xlsx";
    const includeActiveFilters = options.includeActiveFilters ?? false;
    const activeFiltersRows = options.activeFiltersRows ?? null;

    const { fromDay, toDay, dataType, filters } = config;

    // console.log(filters.Comparison);

    let comparison_label =
        COMPARISON_FILTER[filters.Comparison ?? DEFAULT_COMPARISON].label;

    // console.log(comparison_label, filters.Comparison);

    let currencySymbol = get_currency(hotelID);

    let processedData = process(data);

    const wb = new ExcelJS.Workbook();
    const ws = wb.addWorksheet(exportTitle.slice(0, 31));

    ws.addRow([exportTitle]).font = { size: 16 };
    ws.addRow(["Date", `${fromDay} - ${toDay}`]);
    ws.addRow(["Data type", `${dataType}`]);

    ws.addRow([]);

    if (includeActiveFilters && activeFiltersRows) {
        addActiveFiltersBlock(ws, config, activeFiltersRows);
    }

    // Add filters

    // ws.addRow(["Filter key", "Filter value"]);

    // Object.entries(filters).map(([key, value]) => {
    //     if (Array.isArray(value)) {
    //         if (value.length === 0) {
    //             return;
    //         }

    //         const arrayValues = value.join(", ");

    //         ws.addRow([`${key}`, `${arrayValues}`]);
    //     } else {
    //         ws.addRow([`${key}`, `${value}`]);
    //     }
    // });

    const renameKey = (key) => {
        switch (key) {
            case "":
                return "Total";
            case GROUP_OFFERS:
                return "Rate plan";
            case GROUP_ROOM:
                return "Room type";
            case GROUP_COUNTRIES:
                return "Country";
            case GROUP_VOUCHERS:
                return "Vouchers";
            case GROUP_EXTRAS:
                return "Extras";
            default:
                return key;
        }
    };

    Object.entries(processedData).forEach(([key, object]) => {
        const hidesVoucher = !!(
            HOTELS_INFO[hotelID].hide && HOTELS_INFO[hotelID].hide.Vouchers
        );

        const isVoucher = key === GROUP_VOUCHERS;
        const isExtras = key === GROUP_EXTRAS;

        // Add the header row with column headers
        let headerRowValues = [renameKey(key)];

        if (isVoucher && hidesVoucher) {
            // If the hotel has not vouchers, we hide it when the entry is a voucher
            return;
        }

        ws.addRow();

        if (isVoucher) {
            headerRowValues.push(
                "Vouchers sold", // vouchersBookings
                "+/- (%)",
                `${comparison_label}`,

                "Vouchers purchased", // vouchersCount
                "+/- (%)",
                `${comparison_label}`,

                "Revenue", // revenueTotal
                "+/- (%)",
                `${comparison_label}`,

                "Vouchers min", // voucherMin
                "+/- (%)",
                `${comparison_label}`,

                "Vouchers max", // voucherMax
                "+/- (%)",
                `${comparison_label}`,
            );
        } else if (isExtras) {
            headerRowValues.push(
                "Total purchased", // countSelectedExtra
                "+/- (%)",
                `${comparison_label}`,

                "Extras Revenue", // revenueSelectedExtra
                "+/- (%)",
                `${comparison_label}`,

                "Bookings", // bookingsRooms
                "+/- (%)",
                `${comparison_label}`,

                "Room Nights", // roomNightsTotal
                "+/- (%)",
                `${comparison_label}`,
            );
        } else {
            headerRowValues.push(
                "Bookings",
                "+/- (%)",
                `${comparison_label}`,

                "Room Nights",
                "+/- (%)",
                `${comparison_label}`,

                "Room Revenue",
                "+/- (%)",
                `${comparison_label}`,

                "Avg. length of stay",
                "+/- (%)",
                `${comparison_label}`,

                "ADR",
                "+/- (%)",
                `${comparison_label}`,

                "Avg. lead time",
                "+/- (%)",
                `${comparison_label}`,

                "Cancelled Reservations",
                "+/- (%)",
                `${comparison_label}`,
            );
        }

        const headerRow = ws.addRow(headerRowValues);

        // Style the header row
        headerRow.eachCell((cell) => {
            cell.fill = {
                type: "pattern",
                pattern: "solid",
                fgColor: { argb: "FF0070C0" },
            };
            cell.font = {
                color: { argb: "FFFFFFFF" },
            };
        });

        // Initialize totals
        let totals = {
            // Vouchers
            vouchersCount: 0,
            vouchersCountYearBefore: 0,
            vouchersBookings: 0,
            vouchersBookingsYearBefore: 0,
            voucherMin: 999999,
            voucherMax: 0,
            // General
            bookingsTotal: 0,
            bookingsTotalYearBefore: 0,
            roomNightsTotal: 0,
            roomNightsTotalYearBefore: 0,
            revenueTotal: 0,
            revenueTotalYearBefore: 0,
            ALoSTotal: 0,
            ALoSTotalYearBefore: 0,
            ADRTotal: 0,
            ADRTotalYearBefore: 0,
            ABWTotal: 0,
            ABWTotalYearBefore: 0,
            bookingsCancelled: 0,
            bookingsCancelledYearBefore: 0,
            // Extras
            countSelectedExtra: 0,
            countSelectedExtraYearBefore: 0,
            revenueSelectedExtra: 0,
            revenueSelectedExtraYearBefore: 0,
            bookingsRooms: 0,
            bookingsRoomsYearBefore: 0,
        };

        object.forEach(
            ({
                name,
                bookingsTotal,
                bookingsTotalYearBefore,
                roomNightsTotal,
                roomNightsTotalYearBefore,
                revenueTotal,
                revenueTotalYearBefore,
                ALoSTotal,
                ALoSTotalYearBefore,
                ADRTotal,
                ADRTotalYearBefore,
                ABWTotal,
                ABWTotalYearBefore,
                bookingsCancelled,
                bookingsCancelledYearBefore,
                // Vouchers
                vouchersCount,
                vouchersCountYearBefore,
                vouchersBookings,
                vouchersBookingsYearBefore,
                voucherMin,
                voucherMax,
                voucherMinYearBefore,
                voucherMaxYearBefore,
                // Extras
                countSelectedExtra,
                countSelectedExtraYearBefore,
                revenueSelectedExtra,
                revenueSelectedExtraYearBefore,
                bookingsRooms,
                bookingsRoomsYearBefore,
            }) => {
                if (name === "Voucher" || (isVoucher && name === "")) {
                    return;
                }

                // Update totals
                totals.bookingsTotal += isNaN(bookingsTotal)
                    ? 0
                    : bookingsTotal;
                totals.bookingsTotalYearBefore += isNaN(bookingsTotalYearBefore)
                    ? 0
                    : bookingsTotalYearBefore;
                totals.roomNightsTotal += isNaN(roomNightsTotal)
                    ? 0
                    : roomNightsTotal;
                totals.roomNightsTotalYearBefore += isNaN(
                    roomNightsTotalYearBefore,
                )
                    ? 0
                    : roomNightsTotalYearBefore;
                totals.revenueTotal += isNaN(revenueTotal) ? 0 : revenueTotal;
                totals.revenueTotalYearBefore += isNaN(revenueTotalYearBefore)
                    ? 0
                    : revenueTotalYearBefore;
                totals.ALoSTotal += isNaN(ALoSTotal) ? 0 : ALoSTotal;
                totals.ALoSTotalYearBefore += isNaN(ALoSTotalYearBefore)
                    ? 0
                    : ALoSTotalYearBefore;
                totals.ADRTotal += isNaN(ADRTotal) ? 0 : ADRTotal;
                totals.ADRTotalYearBefore += isNaN(ADRTotalYearBefore)
                    ? 0
                    : ADRTotalYearBefore;
                totals.ABWTotal += isNaN(ABWTotal) ? 0 : ABWTotal;
                totals.ABWTotalYearBefore += isNaN(ABWTotalYearBefore)
                    ? 0
                    : ABWTotalYearBefore;
                totals.bookingsCancelled += isNaN(bookingsCancelled)
                    ? 0
                    : bookingsCancelled;
                totals.bookingsCancelledYearBefore += isNaN(
                    bookingsCancelledYearBefore,
                )
                    ? 0
                    : bookingsCancelledYearBefore;

                let rowValues = [name];

                if (isVoucher) {
                    totals.vouchersCount += isNaN(vouchersCount)
                        ? 0
                        : vouchersCount;
                    totals.vouchersCountYearBefore += isNaN(
                        vouchersCountYearBefore,
                    )
                        ? 0
                        : vouchersCountYearBefore;

                    totals.vouchersBookings += isNaN(vouchersBookings)
                        ? 0
                        : vouchersBookings;
                    totals.vouchersBookingsYearBefore += isNaN(
                        vouchersBookingsYearBefore,
                    )
                        ? 0
                        : vouchersBookingsYearBefore;

                    totals.voucherMin = Math.min(totals.voucherMin, voucherMin);
                    totals.voucherMax = Math.max(totals.voucherMax, voucherMax);

                    rowValues.push(
                        // vouchersCount
                        isNaN(vouchersCount) ? 0 : vouchersCount,
                        isNaN(vouchersCountYearBefore)
                            ? 0
                            : calcPercentage(
                                  vouchersCount,
                                  vouchersCountYearBefore,
                              ),
                        isNaN(vouchersCountYearBefore)
                            ? 0
                            : vouchersCountYearBefore,

                        // vouchersBookings
                        isNaN(vouchersBookings) ? 0 : vouchersBookings,
                        isNaN(vouchersBookingsYearBefore)
                            ? 0
                            : calcPercentage(
                                  vouchersBookings,
                                  vouchersBookingsYearBefore,
                              ),
                        isNaN(vouchersBookingsYearBefore)
                            ? 0
                            : vouchersBookingsYearBefore,

                        // revenueTotal
                        isNaN(revenueTotal)
                            ? 0
                            : `${currencySymbol}${revenueTotal}`,

                        isNaN(revenueTotalYearBefore)
                            ? 0
                            : calcPercentage(
                                  revenueTotal,
                                  revenueTotalYearBefore,
                              ),
                        isNaN(revenueTotalYearBefore)
                            ? 0
                            : `${currencySymbol}${revenueTotalYearBefore}`,

                        // voucherMin
                        isNaN(voucherMin)
                            ? 0
                            : `${currencySymbol}${voucherMin}`,

                        isNaN(voucherMinYearBefore)
                            ? 0
                            : calcPercentage(voucherMin, voucherMinYearBefore),
                        isNaN(voucherMinYearBefore)
                            ? 0
                            : `${currencySymbol}${voucherMinYearBefore}`,

                        // voucherMax
                        isNaN(voucherMax)
                            ? 0
                            : `${currencySymbol}${voucherMax}`,

                        isNaN(voucherMaxYearBefore)
                            ? 0
                            : calcPercentage(voucherMax, voucherMaxYearBefore),
                        isNaN(voucherMaxYearBefore)
                            ? 0
                            : `${currencySymbol}${voucherMaxYearBefore}`,
                    );
                } else if (isExtras) {
                    totals.countSelectedExtra += isNaN(countSelectedExtra)
                        ? 0
                        : countSelectedExtra;
                    totals.countSelectedExtraYearBefore += isNaN(
                        countSelectedExtraYearBefore,
                    )
                        ? 0
                        : countSelectedExtraYearBefore;

                    totals.revenueSelectedExtra += isNaN(revenueSelectedExtra)
                        ? 0
                        : revenueSelectedExtra;
                    totals.revenueSelectedExtraYearBefore += isNaN(
                        revenueSelectedExtraYearBefore,
                    )
                        ? 0
                        : revenueSelectedExtraYearBefore;

                    totals.bookingsRooms += isNaN(bookingsRooms)
                        ? 0
                        : bookingsRooms;
                    totals.bookingsRoomsYearBefore += isNaN(
                        bookingsRoomsYearBefore,
                    )
                        ? 0
                        : bookingsRoomsYearBefore;

                    rowValues.push(
                        // countSelectedExtra
                        isNaN(countSelectedExtra) ? 0 : countSelectedExtra,
                        isNaN(countSelectedExtraYearBefore)
                            ? 0
                            : calcPercentage(
                                  countSelectedExtra,
                                  countSelectedExtraYearBefore,
                              ),
                        isNaN(countSelectedExtraYearBefore)
                            ? 0
                            : countSelectedExtraYearBefore,

                        // revenueSelectedExtra
                        isNaN(revenueSelectedExtra)
                            ? 0
                            : `${currencySymbol}${revenueSelectedExtra}`,
                        isNaN(revenueSelectedExtraYearBefore)
                            ? 0
                            : calcPercentage(
                                  revenueSelectedExtra,
                                  revenueSelectedExtraYearBefore,
                              ),
                        isNaN(revenueSelectedExtraYearBefore)
                            ? 0
                            : `${currencySymbol}${revenueSelectedExtraYearBefore}`,

                        // bookingsRooms
                        isNaN(bookingsRooms) ? 0 : bookingsRooms,
                        isNaN(bookingsRoomsYearBefore)
                            ? 0
                            : calcPercentage(
                                  bookingsRooms,
                                  bookingsRoomsYearBefore,
                              ),
                        isNaN(bookingsRoomsYearBefore)
                            ? 0
                            : bookingsRoomsYearBefore,

                        // roomNightsTotal
                        isNaN(roomNightsTotal) ? 0 : roomNightsTotal,
                        isNaN(roomNightsTotalYearBefore)
                            ? 0
                            : calcPercentage(
                                  roomNightsTotal,
                                  roomNightsTotalYearBefore,
                              ),
                        isNaN(roomNightsTotalYearBefore)
                            ? 0
                            : roomNightsTotalYearBefore,
                    );
                } else {
                    rowValues.push(
                        isNaN(bookingsTotal) ? 0 : bookingsTotal,
                        isNaN(bookingsTotalYearBefore)
                            ? 0
                            : calcPercentage(
                                  bookingsTotal,
                                  bookingsTotalYearBefore,
                              ),
                        isNaN(bookingsTotalYearBefore)
                            ? 0
                            : bookingsTotalYearBefore,
                        isNaN(roomNightsTotal) ? 0 : roomNightsTotal,
                        isNaN(roomNightsTotalYearBefore)
                            ? 0
                            : calcPercentage(
                                  roomNightsTotal,
                                  roomNightsTotalYearBefore,
                              ),
                        isNaN(roomNightsTotalYearBefore)
                            ? 0
                            : roomNightsTotalYearBefore,

                        isNaN(revenueTotal)
                            ? 0
                            : `${currencySymbol}${revenueTotal}`,
                        isNaN(revenueTotalYearBefore)
                            ? 0
                            : calcPercentage(
                                  revenueTotal,
                                  revenueTotalYearBefore,
                              ),
                        isNaN(revenueTotalYearBefore)
                            ? 0
                            : `${currencySymbol}${revenueTotalYearBefore}`,

                        isNaN(ALoSTotal) ? 0 : ALoSTotal,
                        isNaN(ALoSTotalYearBefore)
                            ? 0
                            : calcPercentage(ALoSTotal, ALoSTotalYearBefore),
                        isNaN(ALoSTotalYearBefore) ? 0 : ALoSTotalYearBefore,

                        isNaN(ADRTotal) ? 0 : `${currencySymbol}${ADRTotal}`,
                        isNaN(ADRTotalYearBefore)
                            ? 0
                            : calcPercentage(ADRTotal, ADRTotalYearBefore),
                        isNaN(ADRTotalYearBefore)
                            ? 0
                            : `${currencySymbol}${ADRTotalYearBefore}`,

                        isNaN(ABWTotal) ? 0 : ABWTotal,
                        isNaN(ABWTotalYearBefore)
                            ? 0
                            : calcPercentage(ABWTotal, ABWTotalYearBefore),
                        isNaN(ABWTotalYearBefore) ? 0 : ABWTotalYearBefore,

                        isNaN(bookingsCancelled) ? 0 : bookingsCancelled,
                        isNaN(bookingsCancelledYearBefore)
                            ? 0
                            : calcPercentage(
                                  bookingsCancelled,
                                  bookingsCancelledYearBefore,
                              ),
                        isNaN(bookingsCancelledYearBefore)
                            ? 0
                            : bookingsCancelledYearBefore,
                    );
                }

                const dataRow = ws.addRow(rowValues);

                function formatCell(cell, value, index) {
                    cell.alignment = {
                        vertical: "middle",
                        // horizontal: "center",
                    };

                    if (![3, 6, 9, 12, 15, 18, 21].includes(index)) return;

                    let fillColor;
                    if ([3, 6, 9, 12, 15].includes(index)) {
                        fillColor =
                            value < 0
                                ? "FFF2C6DE"
                                : value > 0
                                  ? "FFC9E4DE"
                                  : "FFFAEDCB";
                    } else if ([18, 21].includes(index)) {
                        fillColor =
                            value < 0
                                ? "FFC9E4DE"
                                : value > 0
                                  ? "FFF2C6DE"
                                  : "FFFAEDCB";
                    }

                    cell.fill = {
                        type: "pattern",
                        pattern: "solid",
                        fgColor: { argb: fillColor },
                    };
                }

                dataRow.eachCell((cell, index) => {
                    let value = cell.value;
                    formatCell(cell, value, index);
                });
            },
        );

        if (renameKey(key) !== "Total") {
            let totalsRowValues = ["Total"];

            if (isVoucher) {
                // Vouchers purchased (vouchersCount)
                totalsRowValues.push(
                    totals.vouchersCount,
                    calcPercentage(
                        totals.vouchersCount,
                        totals.vouchersCountYearBefore,
                    ),
                    totals.vouchersCountYearBefore,
                );

                // Vouchers sold (vouchersBookings)
                totalsRowValues.push(
                    totals.vouchersBookings,
                    calcPercentage(
                        totals.vouchersBookings,
                        totals.vouchersBookingsYearBefore,
                    ),
                    totals.vouchersBookingsYearBefore,
                );

                // Revenue
                totalsRowValues.push(
                    `${currencySymbol}${totals.revenueTotal}`,
                    calcPercentage(
                        totals.revenueTotal,
                        totals.revenueTotalYearBefore,
                    ),
                    `${currencySymbol}${totals.revenueTotalYearBefore}`,
                );
            } else if (isExtras) {
                totalsRowValues.push(
                    totals.countSelectedExtra,
                    calcPercentage(
                        totals.countSelectedExtra,
                        totals.countSelectedExtraYearBefore,
                    ),
                    totals.countSelectedExtraYearBefore,

                    `${currencySymbol}${totals.revenueSelectedExtra}`,
                    calcPercentage(
                        totals.revenueSelectedExtra,
                        totals.revenueSelectedExtraYearBefore,
                    ),
                    `${currencySymbol}${totals.revenueSelectedExtraYearBefore}`,

                    totals.bookingsRooms,
                    calcPercentage(
                        totals.bookingsRooms,
                        totals.bookingsRoomsYearBefore,
                    ),
                    totals.bookingsRoomsYearBefore,

                    totals.roomNightsTotal,
                    calcPercentage(
                        totals.roomNightsTotal,
                        totals.roomNightsTotalYearBefore,
                    ),
                    totals.roomNightsTotalYearBefore,
                );
            } else {
                totalsRowValues.push(
                    totals.bookingsTotal,
                    calcPercentage(
                        totals.bookingsTotal,
                        totals.bookingsTotalYearBefore,
                    ),
                    totals.bookingsTotalYearBefore,
                );

                totalsRowValues.push(
                    totals.roomNightsTotal,
                    calcPercentage(
                        totals.roomNightsTotal,
                        totals.roomNightsTotalYearBefore,
                    ),
                    totals.roomNightsTotalYearBefore,

                    `${currencySymbol}${totals.revenueTotal}`,
                    calcPercentage(
                        totals.revenueTotal,
                        totals.revenueTotalYearBefore,
                    ),
                    `${currencySymbol}${totals.revenueTotalYearBefore}`,

                    totals.ALoSTotal,
                    calcPercentage(
                        totals.ALoSTotal,
                        totals.ALoSTotalYearBefore,
                    ),
                    totals.ALoSTotalYearBefore,

                    `${currencySymbol}${totals.ADRTotal}`,
                    calcPercentage(totals.ADRTotal, totals.ADRTotalYearBefore),
                    `${currencySymbol}${totals.ADRTotalYearBefore}`,

                    totals.ABWTotal,
                    calcPercentage(totals.ABWTotal, totals.ABWTotalYearBefore),
                    totals.ABWTotalYearBefore,

                    totals.bookingsCancelled,
                    calcPercentage(
                        totals.bookingsCancelled,
                        totals.bookingsCancelledYearBefore,
                    ),
                    totals.bookingsCancelledYearBefore,
                );
            }

            const totalsRow = ws.addRow(totalsRowValues);

            // Style the totals row
            totalsRow.eachCell((cell) => {
                cell.font = {
                    bold: true,
                };
            });
        }
    });

    resizeColumns(ws);

    createBlob(wb, fileName);
};

const styleSectionTitle = (cell, size = 12) => {
    cell.font = {
        bold: true,
        size,
    };
};

const styleTableHeader = (row) => {
    row.eachCell((cell) => {
        cell.fill = {
            type: "pattern",
            pattern: "solid",
            fgColor: { argb: "FF0070C0" },
        };
        cell.font = {
            color: { argb: "FFFFFFFF" },
            bold: true,
        };
    });
};

const getTrafficLightFillColor = ({ value, inverted = false }) => {
    if (value === 0) return "FFFAEDCB";

    const isPositive = value > 0;

    if (inverted) {
        return isPositive ? "FFF2C6DE" : "FFC9E4DE";
    }

    return isPositive ? "FFC9E4DE" : "FFF2C6DE";
};

const normalizeTableCellValue = (value) => {
    if (
        value &&
        typeof value === "object" &&
        !Array.isArray(value) &&
        (Object.prototype.hasOwnProperty.call(value, "value") ||
            Object.prototype.hasOwnProperty.call(value, "style"))
    ) {
        return {
            displayValue: formatExportValue(value.value),
            style: value.style ?? null,
        };
    }

    return {
        displayValue: formatExportValue(value),
        style: null,
    };
};

const applyTableCellStyle = (cell, style) => {
    if (!style) return;

    cell.alignment = {
        vertical: "middle",
        ...(style.alignment ?? {}),
    };

    if (style.font) {
        cell.font = style.font;
    }

    if (style.fill) {
        cell.fill = style.fill;
    }

    if (style.trafficLight) {
        cell.fill = {
            type: "pattern",
            pattern: "solid",
            fgColor: {
                argb: getTrafficLightFillColor(style.trafficLight),
            },
        };
    }
};

const renderSectionRows = (ws, rows) => {
    (rows ?? []).forEach((row) => {
        if (Array.isArray(row)) {
            ws.addRow(row.map((value) => formatExportValue(value)));
            return;
        }

        if (row && typeof row === "object") {
            ws.addRow([
                formatExportValue(row.label ?? row.key ?? ""),
                formatExportValue(row.value),
            ]);
        }
    });
};

const renderTableSection = (ws, section) => {
    if (section.title) {
        const titleRow = ws.addRow([section.title]);
        styleSectionTitle(titleRow.getCell(1), 12);
    }

    const columns = section.columns ?? section.headers ?? [];
    const normalizedColumns = columns.map((column) =>
        typeof column === "string"
            ? column
            : (column.label ?? column.title ?? column.key ?? ""),
    );

    if (normalizedColumns.length > 0) {
        const headerRow = ws.addRow(normalizedColumns);
        styleTableHeader(headerRow);
    }

    (section.rows ?? []).forEach((row) => {
        if (Array.isArray(row)) {
            const normalizedCells = row.map((value) =>
                normalizeTableCellValue(value),
            );
            const addedRow = ws.addRow(
                normalizedCells.map((cell) => cell.displayValue),
            );
            normalizedCells.forEach((cellValue, index) => {
                applyTableCellStyle(
                    addedRow.getCell(index + 1),
                    cellValue.style,
                );
            });
            return;
        }

        if (row && typeof row === "object") {
            const normalizedCells = (columns ?? []).map((column) => {
                if (typeof column === "string") {
                    return normalizeTableCellValue(row[column]);
                }

                const key =
                    column.key ?? column.field ?? column.id ?? column.label;
                return normalizeTableCellValue(key ? row[key] : undefined);
            });

            const addedRow = ws.addRow(
                normalizedCells.map((cell) => cell.displayValue),
            );
            normalizedCells.forEach((cellValue, index) => {
                applyTableCellStyle(
                    addedRow.getCell(index + 1),
                    cellValue.style,
                );
            });
        }
    });
};

export const exportScreen = ({
    sections = [],
    worksheetName = "Export",
    fileName = "Export.xlsx",
}) => {
    const wb = new ExcelJS.Workbook();
    const ws = wb.addWorksheet(worksheetName.slice(0, 31));

    sections.filter(Boolean).forEach((section) => {
        if (section.kind === "title") {
            const titleRow = ws.addRow([
                formatExportValue(section.value ?? section.title ?? ""),
            ]);
            styleSectionTitle(titleRow.getCell(1), 16);
            ws.addRow([]);
            return;
        }

        if (section.kind === "keyValue") {
            if (section.title) {
                const titleRow = ws.addRow([section.title]);
                styleSectionTitle(titleRow.getCell(1), 12);
            }

            renderSectionRows(ws, section.rows);
            ws.addRow([]);
            return;
        }

        if (section.kind === "table") {
            renderTableSection(ws, section);
            ws.addRow([]);
        }
    });

    resizeColumns(ws);
    createBlob(wb, fileName);
};

// Resize all columns in the worksheet

const resizeColumns = (ws) => {
    ws.columns.forEach(function (column, i) {
        let maxLength = 0;
        column["eachCell"]({ includeEmpty: true }, function (cell) {
            var columnLength = cell.value ? cell.value.toString().length : 12;
            if (columnLength > maxLength) {
                maxLength = columnLength;
            }
        });
        column.width = maxLength < 12 ? 12 : maxLength;
    });
};

// Create blob (to download the Excel export)

const createBlob = (wb, fileName = "FullDataExport.xlsx") => {
    const cb = async () => {
        const blob = await wb.xlsx.writeBuffer();

        const blobUrl = URL.createObjectURL(
            new Blob([blob], {
                type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
            }),
        );
        const a = document.createElement("a");
        a.href = blobUrl;
        a.download = fileName;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(blobUrl);
    };

    cb();
};

const addActiveFiltersBlock = (ws, config, activeFiltersRows = null) => {
    if (Array.isArray(activeFiltersRows) && activeFiltersRows.length > 0) {
        ws.addRow(["Active filters"]).font = { bold: true };
        activeFiltersRows.forEach((row) => {
            ws.addRow([
                formatExportValue(row?.label, ""),
                formatExportValue(row?.value),
            ]);
        });
        ws.addRow([]);
        return;
    }

    const {
        filters = {},
        presetDate,
        presetArrivalDate,
        presetResDate,
        fromArrivalDay,
        toArrivalDay,
        fromResDateDay,
        toResDateDay,
    } = config;

    ws.addRow(["Active filters"]).font = { bold: true };
    ws.addRow(["Main preset", formatExportValue(presetDate)]);
    ws.addRow(["Arrival preset", formatExportValue(presetArrivalDate)]);
    ws.addRow(["Booking preset", formatExportValue(presetResDate)]);
    ws.addRow([
        "Arrival range",
        `${formatExportValue(fromArrivalDay)} - ${formatExportValue(toArrivalDay)}`,
    ]);
    ws.addRow([
        "Booking range",
        `${formatExportValue(fromResDateDay)} - ${formatExportValue(toResDateDay)}`,
    ]);

    Object.keys(filters)
        .sort((a, b) => a.localeCompare(b))
        .forEach((key) => {
            const value = filters[key];
            if (Array.isArray(value) && value.length === 0) return;
            if (value === null || value === undefined || value === "") return;

            ws.addRow([key, formatExportValue(value)]);
        });

    ws.addRow([]);
};

const formatExportValue = (value) => {
    if (Array.isArray(value)) return value.join(", ");
    if (value && typeof value === "object") return JSON.stringify(value);
    if (value === null || value === undefined || value === "") return "Any";
    return String(value);
};

const process = (data) => {
    delete data["Type"];

    const order = {
        "": null,
        OfferTitle: null,
        RoomName: null,
        Country: null,
        VoucherName: null,
    };

    const reordered = Object.assign(order, data);

    let auxProcessedData = {};

    Object.entries(reordered).forEach(([key, object]) => {
        auxProcessedData[key] = [];

        Object.entries(object).forEach(([subKey, objs]) => {
            let processedObj = { name: subKey, ...objs["result"] };

            // Appending "YearBefore" suffix to keys from objs["resultOneYearBefore"]
            if (objs["resultOneYearBefore"]) {
                Object.keys(objs["resultOneYearBefore"]).forEach((prop) => {
                    processedObj[`${prop}YearBefore`] =
                        objs["resultOneYearBefore"][prop];
                });
            }

            auxProcessedData[key].push(processedObj);
        });

        auxProcessedData[key].sort((a, b) => b.bookings - a.bookings);
    });

    return auxProcessedData;
};

const calcPercentage = (actual, comparison) => {
    actual = actual ?? 0;
    comparison = comparison ?? 0;

    if (comparison === 0) comparison = 1;

    return Math.round(((actual - comparison) / comparison) * 100, 2);
};
